import React, { JSX, useEffect, useMemo, useState } from 'react';

import {
  EventOperatorRow,
  CreateEventFormState,
  EventRow,
  EventUserOption,
  WorldTriathlonEventOption,
} from '../types';
import {
  BTN_SECONDARY,
  formatOperatorLabel,
  formatUserOptionLabel,
} from '../utils';
import { formatUserIdentityDisplay } from '../../../helpers/helpers';
import { PortaledLayer } from '../../shared/PortaledLayer';
import InlineSpinner from '../../shared/InlineSpinner';

type ModalShellProps = {
  title: string;
  description?: string;
  /** Tailwind max-width class for the dialog panel (default suits multi-column dialogs). */
  maxWidthClass?: string;
  onClose: () => void;
  children: React.ReactNode;
};

function ModalShell(props: ModalShellProps): JSX.Element {
  const { title, description, maxWidthClass = 'max-w-4xl', onClose, children } = props;

  return (
    <PortaledLayer>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
        <div className={`max-h-[90vh] w-full ${maxWidthClass} overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100`}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">{title}</div>
            {description ? (
              <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{description}</div>
            ) : null}
          </div>

          <button type="button" onClick={onClose} className={BTN_SECONDARY}>
            Close
          </button>
        </div>

        <div className="max-h-[calc(90vh-65px)] overflow-auto bg-white dark:bg-slate-900">{children}</div>
      </div>
      </div>
    </PortaledLayer>
  );
}

type CreateEventDialogProps = {
  open: boolean;
  form: CreateEventFormState;
  wtEvents: WorldTriathlonEventOption[];
  wtLoading: boolean;
  creatingEvent: boolean;
  onClose: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onRefreshWtEvents: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
};

export function CreateEventDialog(props: CreateEventDialogProps): JSX.Element | null {
  const {
    open,
    form,
    wtEvents,
    wtLoading,
    creatingEvent,
    onClose,
    onChange,
    onRefreshWtEvents,
    onSubmit,
  } = props;
  if (!open) return null;

  return (
    <ModalShell title="Create new event" maxWidthClass="max-w-lg" onClose={onClose}>
      <div className="p-4">
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
              Event name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={onChange}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              placeholder="e.g. Ironman Taupo 2026"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                  World Triathlon event (optional)
                </label>
                <button
                  type="button"
                  onClick={onRefreshWtEvents}
                  disabled={wtLoading}
                  className={BTN_SECONDARY}
                >
                  {wtLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              <select
                name="wtEventId"
                value={form.wtEventId}
                onChange={onChange}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              >
                <option value="">None</option>
                {wtEvents.map((event) => (
                  <option key={event.eventId} value={event.eventId}>
                    {event.title}
                    {event.date ? ` (${event.date})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                Start time (optional)
              </label>
              <input
                type="datetime-local"
                name="start"
                value={form.start}
                onChange={onChange}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                End time (optional)
              </label>
              <input
                type="datetime-local"
                name="end"
                value={form.end}
                onChange={onChange}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={BTN_SECONDARY}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingEvent}
              className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded border border-blue-700 bg-blue-600 text-xs font-semibold text-white whitespace-nowrap disabled:opacity-50"
            >
              {creatingEvent ? (
                <>
                  <InlineSpinner size="xs" variant="current" />
                  Creating...
                </>
              ) : (
                'Create event'
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}

type OperatorIntroDialogProps = {
  open: boolean;
  userName?: string | null;
  onClose: () => void;
};

export function OperatorIntroDialog(props: OperatorIntroDialogProps): JSX.Element | null {
  const { open, userName, onClose } = props;
  if (!open) return null;

  return (
    <ModalShell
      title="Operator guide"
      description="Choose your race before moving into the operator view."
      onClose={onClose}
    >
      <div className="p-4 space-y-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100">
          {userName ? (
            <span>
              Signed in as <span className="font-semibold">{userName}</span>.
            </span>
          ) : (
            <span>Signed in as an operator.</span>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
          <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">What to do</div>
          <ol className="mt-3 space-y-3 text-sm text-gray-700 dark:text-slate-300 list-decimal pl-5">
            <li>Review the events you have been assigned to.</li>
            <li>Click `Select` on the event you want to work on.</li>
            <li>Use `Show details` if you want to check devices or operators first.</li>
            <li>Click `Continue` to move into the operator-scoped device and logs view.</li>
          </ol>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
          You will only see devices, logs, and actions for the selected event.
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 px-3 rounded border border-blue-700 bg-blue-600 text-xs font-semibold text-white whitespace-nowrap"
          >
            Got it
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

type AssignOperatorDialogProps = {
  open: boolean;
  eventId: string | null;
  events: EventRow[];
  assignedOperators: EventOperatorRow[];
  availableUsers: EventUserOption[];
  selectedUserId: string;
  selectedRole: 'ADMIN' | 'OPERATOR';
  assigningOperator: boolean;
  onClose: () => void;
  onUserChange: (value: string) => void;
  onRoleChange: (value: 'ADMIN' | 'OPERATOR') => void;
  onAssign: () => Promise<void>;
};

export function AssignOperatorDialog(props: AssignOperatorDialogProps): JSX.Element | null {
  const {
    open,
    eventId,
    events,
    assignedOperators,
    availableUsers,
    selectedUserId,
    selectedRole,
    assigningOperator,
    onClose,
    onUserChange,
    onRoleChange,
    onAssign,
  } = props;

  if (!open || !eventId) return null;
  const eventRow = events.find((e) => e.eventId === eventId) ?? null;
  const eventName = String(eventRow?.name ?? '').trim();
  const eventLabel = eventName || eventId;
  const [search, setSearch] = useState('');

  const assignedUserIds = useMemo(
    () => new Set(assignedOperators.map((op) => String(op.userId ?? '').trim()).filter(Boolean)),
    [assignedOperators],
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...availableUsers].sort((a, b) =>
      formatUserOptionLabel(a).localeCompare(formatUserOptionLabel(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
    if (!q) return sorted;
    return sorted.filter((user) => {
      const label = formatUserOptionLabel(user).toLowerCase();
      const userId = String(user.userId ?? '').toLowerCase();
      const email = String(user.email ?? '').toLowerCase();
      const username = String(user.username ?? '').toLowerCase();
      return (
        label.includes(q) ||
        userId.includes(q) ||
        email.includes(q) ||
        username.includes(q)
      );
    });
  }, [availableUsers, search]);

  const selectedUser = useMemo(
    () => availableUsers.find((user) => user.userId === selectedUserId) ?? null,
    [availableUsers, selectedUserId],
  );

  const userIdToDisplayName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const user of availableUsers) {
      const id = String(user.userId ?? '').trim();
      if (!id) continue;
      map[id] = formatUserOptionLabel(user);
    }
    for (const operator of assignedOperators) {
      const id = String(operator.userId ?? '').trim();
      if (!id) continue;
      if (!map[id]) map[id] = formatOperatorLabel(operator);
    }
    return map;
  }, [assignedOperators, availableUsers]);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  return (
    <ModalShell
      title="Assign operator"
      description={`Choose a user and role for ${eventLabel}.`}
      onClose={onClose}
    >
      <div className="p-4 space-y-4">
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
              <span className="text-gray-500 dark:text-slate-400">Event</span>
              <span className="font-semibold text-gray-900 dark:text-slate-100">{eventLabel}</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
              <span className="text-gray-500 dark:text-slate-400">Event ID</span>
              <span className="font-mono text-gray-900 dark:text-slate-100">{eventId}</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
              <span className="text-gray-500 dark:text-slate-400">Assigned now</span>
              <span className="font-semibold text-gray-900 dark:text-slate-100">{assignedOperators.length}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">Find user</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, username, or user ID"
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-xs font-semibold text-gray-700 dark:text-slate-300">Available users</div>
                <div className="text-[11px] text-gray-500 dark:text-slate-400">{filteredUsers.length} match(es)</div>
              </div>

              <div className="max-h-72 overflow-auto divide-y divide-gray-100 dark:divide-slate-800">
                {filteredUsers.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">No users match this search.</div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = user.userId === selectedUserId;
                    const alreadyAssigned = assignedUserIds.has(user.userId);
                    return (
                      <button
                        key={user.userId}
                        type="button"
                        onClick={() => onUserChange(user.userId)}
                        disabled={alreadyAssigned}
                        className={[
                          'w-full px-3 py-3 text-left transition-colors',
                          alreadyAssigned
                            ? 'cursor-not-allowed bg-gray-50 text-gray-400 dark:bg-slate-800/80 dark:text-slate-500'
                            : isSelected
                              ? 'bg-blue-50 text-gray-900 dark:bg-blue-950/45 dark:text-slate-100'
                              : 'bg-white text-gray-900 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                              {formatUserOptionLabel(user)}
                            </div>
                            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400 break-all">
                              {formatUserIdentityDisplay(user.userId, userIdToDisplayName)}
                            </div>
                          </div>
                          {alreadyAssigned ? (
                            <span className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300">
                              Already assigned
                            </span>
                          ) : isSelected ? (
                            <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800 dark:border-blue-500/70 dark:bg-blue-500/20 dark:text-blue-100">
                              Selected
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
              <div className="text-xs font-semibold text-gray-700 dark:text-slate-300">Assignment</div>

              <div className="mt-3">
                <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">Selected user</label>
                <div className="mt-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                  {selectedUser ? formatUserOptionLabel(selectedUser) : 'Choose a user from the list'}
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">Role</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  value={selectedRole}
                  onChange={(e) => onRoleChange(e.target.value as 'ADMIN' | 'OPERATOR')}
                >
                  <option value="OPERATOR">Operator</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              <div className="mt-3 text-[11px] text-gray-500 dark:text-slate-400">
                `Operator` gives race access. `Admin` also allows managing the event.
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Currently assigned
              </div>
              <div className="max-h-48 overflow-auto divide-y divide-gray-100 dark:divide-slate-800">
                {assignedOperators.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">No operators assigned yet.</div>
                ) : (
                  assignedOperators.map((operator) => (
                    <div key={operator.userId} className="px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                            {formatOperatorLabel(operator)}
                          </div>
                          <div className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400 break-all">
                            {formatUserIdentityDisplay(operator.userId, userIdToDisplayName)}
                          </div>
                        </div>
                        <span className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300">
                          {operator.roleInEvent || 'OPERATOR'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onAssign()}
            disabled={assigningOperator || !selectedUserId}
            className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded border border-blue-700 bg-blue-600 text-xs font-semibold text-white whitespace-nowrap disabled:opacity-50"
          >
            {assigningOperator ? (
              <>
                <InlineSpinner size="xs" variant="current" />
                Assigning...
              </>
            ) : (
              'Assign operator'
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
